import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, Check, ExternalLink, Gift, LifeBuoy, Mail, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, ShoppingBag, StickyNote, Ticket, UserPlus, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { formatCallNumber, type CallPop, type CallPopSignal, type CallSummary } from '@bre-hub/contracts'
import { api, ApiError } from '../api'
import { callsApi } from '../callsApi'
import { commsApi } from '../commsApi'
import { formatDate, initials, useCan, useMe } from '../session'
import { Dialog, PersonPicker } from './ui'

// Call pop (ADR 0038). A WebSocket to the call pop channel says "call X
// changed"; this layer then reads the signed-in person's open pops through
// their own session (so their capabilities decide what they see) and shows
// each as a card at the bottom right. If the socket is down it polls.

const RECONNECT_MAX_MS = 30_000
const PING_MS = 25_000
// A pop nobody touched leaves on its own once the call is answered or over.
const ANSWERED_LINGER_MS = 15_000
const ENDED_LINGER_MS = 12_000
const MISSED_LINGER_MS = 30_000

// ---------------------------------------------------------- live channel

export function useCallChannel(enabled: boolean, onSignal: (signal: CallPopSignal) => void, onOpen: () => void): boolean {
  const [connected, setConnected] = useState(false)
  const signal = useRef(onSignal)
  const open = useRef(onOpen)
  signal.current = onSignal
  open.current = onOpen
  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return
    let ws: WebSocket | null = null
    let stopped = false
    let attempt = 0
    let ping: number | undefined
    let retry: number | undefined
    const connect = () => {
      if (stopped) return
      ws = new WebSocket(`${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/calls/live`)
      ws.onopen = () => {
        attempt = 0
        setConnected(true)
        open.current()
        ping = window.setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), PING_MS)
      }
      ws.onmessage = (event) => {
        if (event.data === 'pong') return
        try {
          const parsed = JSON.parse(String(event.data)) as CallPopSignal
          if (parsed?.type === 'call') signal.current(parsed)
        } catch {
          // not ours
        }
      }
      ws.onclose = () => {
        setConnected(false)
        window.clearInterval(ping)
        if (stopped) return
        attempt += 1
        retry = window.setTimeout(connect, Math.min(RECONNECT_MAX_MS, 1000 * 2 ** Math.min(attempt, 5)))
      }
    }
    connect()
    return () => {
      stopped = true
      window.clearInterval(ping)
      window.clearTimeout(retry)
      ws?.close()
    }
  }, [enabled])
  return connected
}

// ------------------------------------------------------------ the layer

export function CallPopLayer() {
  const me = useMe()
  const can = useCan('calls.read')
  const on = can && me.callPop === true
  const queryClient = useQueryClient()
  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: ['call-pops'] }), [queryClient])
  const connected = useCallChannel(on, refresh, refresh)
  const pops = useQuery({ queryKey: ['call-pops'], queryFn: callsApi.pops, enabled: on, refetchInterval: connected ? 30_000 : 6_000, refetchIntervalInBackground: true })
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [pinned, setPinned] = useState<Set<string>>(() => new Set())
  const dismiss = useCallback(
    (id: string) => {
      setHidden((prev) => new Set(prev).add(id))
      callsApi.dismiss(id).catch(() => undefined)
    },
    [],
  )
  const pin = useCallback((id: string) => setPinned((prev) => (prev.has(id) ? prev : new Set(prev).add(id))), [])

  const visible = (pops.data?.pops ?? []).filter((p) => !hidden.has(p.call.id) && !p.dismissed)
  if (!on || visible.length === 0) return null
  return (
    <div className="call-pop-layer" aria-live="polite" aria-label="Calls">
      {visible.map((pop) => (
        <FloatingPop key={pop.call.id} pop={pop} pinned={pinned.has(pop.call.id)} onPin={() => pin(pop.call.id)} onDismiss={() => dismiss(pop.call.id)} />
      ))}
    </div>
  )
}

function FloatingPop({ pop, pinned, onPin, onDismiss }: { pop: CallPop; pinned: boolean; onPin: () => void; onDismiss: () => void }) {
  const state = pop.call.state
  // Untouched pops leave by themselves after the call is answered or over.
  useEffect(() => {
    if (pinned || state === 'ringing') return
    const wait = state === 'answered' ? ANSWERED_LINGER_MS : state === 'missed' ? MISSED_LINGER_MS : ENDED_LINGER_MS
    const t = window.setTimeout(onDismiss, wait)
    return () => window.clearTimeout(t)
  }, [state, pinned, onDismiss])
  return (
    <div className={`call-pop call-pop-${state}`} onPointerDown={onPin} onFocusCapture={onPin}>
      <CallPopCard pop={pop} onDismiss={onDismiss} pinned={pinned} onPin={onPin} floating />
    </div>
  )
}

// ----------------------------------------------------------------- card

function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [active])
  return now
}

export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`
}

function headline(call: CallSummary, now: number): { icon: ReactNode; title: string; detail: string | null } {
  const where = call.direction === 'in' ? (call.lineLabel ?? (call.lineNumber ? formatCallNumber(call.lineNumber) : 'Unmapped line')) : call.extension ? `Ext. ${call.extension}` : null
  const since = (iso: string) => clock((now - Date.parse(iso)) / 1000)
  if (call.state === 'ringing') return { icon: call.direction === 'in' ? <PhoneIncoming size={16} aria-hidden /> : <PhoneOutgoing size={16} aria-hidden />, title: call.direction === 'in' ? 'Incoming call' : 'Calling', detail: [where, `ringing ${since(call.startedAt)}`].filter(Boolean).join(' · ') }
  if (call.state === 'answered') return { icon: <PhoneCall size={16} aria-hidden />, title: call.direction === 'in' ? 'Answered' : 'On the call', detail: [where, call.answeredAt ? since(call.answeredAt) : null].filter(Boolean).join(' · ') }
  if (call.state === 'missed') return { icon: <PhoneMissed size={16} aria-hidden />, title: call.direction === 'in' ? 'Missed call' : 'No answer', detail: where }
  return { icon: <PhoneOff size={16} aria-hidden />, title: 'Call ended', detail: [where, call.durationSeconds !== null ? clock(call.durationSeconds) : null].filter(Boolean).join(' · ') }
}

// Caller ID comes in capitals ("BROWN MATTHEW"); shown in title case.
export function titleCase(name: string): string {
  return name.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase())
}

export function CallPopCard({ pop, onDismiss, pinned, onPin, floating = false }: { pop: CallPop; onDismiss?: () => void; pinned?: boolean; onPin?: () => void; floating?: boolean }) {
  const { call } = pop
  const me = useMe()
  const now = useNow(call.state === 'ringing' || call.state === 'answered')
  const head = headline(call, now)
  const [dialog, setDialog] = useState<'create' | 'other' | null>(null)
  const [noting, setNoting] = useState(false)
  // The one person this call is about, when there is one: the known person,
  // or the only holder of the number.
  const focus = call.person ?? (pop.candidates.length === 1 ? { ...pop.candidates[0]!, method: null } : null)
  const callerId = call.callerName && call.direction === 'in' ? titleCase(call.callerName) : null

  return (
    <article className="call-card" data-state={call.state} data-direction={call.direction} aria-label={`${head.title}${callerId ? `, ${callerId}` : ''}`}>
      <header className="call-card-head">
        <span className="call-state-icon" aria-hidden>
          {head.icon}
        </span>
        <div className="call-card-title">
          <strong>{head.title}</strong>
          {head.detail && <span>{head.detail}</span>}
        </div>
        {floating && call.state === 'answered' && !pinned && (
          <button type="button" className="btn btn-compact call-keep" onClick={onPin} title="Keep this open while you talk">
            I have it
          </button>
        )}
        {onDismiss && (
          <button type="button" className="btn btn-quiet btn-icon" onClick={onDismiss} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
        )}
      </header>

      <div className="call-card-body">
        <div className="call-number">
          <span className="call-number-value">{formatCallNumber(call.outsideNumber)}</span>
          {callerId && <span className="call-callerid">Caller ID: {callerId}</span>}
          {call.direction === 'out' && call.staff && <span className="call-callerid">{call.staff.personId === me.personId ? `Your call from ext. ${call.extension ?? '?'}` : `From ${call.staff.displayName}, ext. ${call.extension ?? '?'}`}</span>}
        </div>

        {pop.detailsHidden ? (
          <p className="faint call-hidden">Caller hidden (needs people.read).</p>
        ) : (
          <Identity pop={pop} onCreate={() => setDialog('create')} onOther={() => setDialog('other')} />
        )}

        {!pop.detailsHidden && focus && <CallerContext personId={focus.personId} />}

        {pop.notes.length > 0 && (
          <ul className="call-notes">
            {pop.notes.map((n) => (
              <li key={n.id}>
                <span>{n.body}</span>
                <span className="faint">
                  {n.authorName ?? 'Someone'} · {formatDate(n.createdAt, true)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {noting ? <NoteForm callId={call.id} onDone={() => setNoting(false)} /> : null}
      </div>

      <footer className="call-card-foot">
        {focus && !pop.detailsHidden && (
          <Link className="btn btn-compact" to={`/people/${focus.personId}`}>
            <ExternalLink size={14} aria-hidden /> Open person
          </Link>
        )}
        {!noting && pop.can.note && (
          <button type="button" className="btn btn-compact" onClick={() => setNoting(true)}>
            <StickyNote size={14} aria-hidden /> Add note
          </button>
        )}
        <Link className="btn btn-quiet btn-compact call-log-link" to={call.callbackStatus === 'needed' ? '/calls?filter=callbacks' : '/calls'}>
          {call.callbackStatus === 'needed' ? 'On the call-back list' : 'Call log'}
        </Link>
      </footer>

      {dialog === 'create' && <CreatePersonDialog pop={pop} suggested={callerId ?? ''} onClose={() => setDialog(null)} />}
      {dialog === 'other' && <ConfirmOtherDialog pop={pop} onClose={() => setDialog(null)} />}
    </article>
  )
}

function usePopMutation() {
  const queryClient = useQueryClient()
  return (pop: CallPop) => {
    queryClient.setQueryData<{ pops: CallPop[] }>(['call-pops'], (prev) => (prev ? { pops: prev.pops.map((p) => (p.call.id === pop.call.id ? pop : p)) } : prev))
    queryClient.setQueryData(['call-pop', pop.call.id], (prev: { pop: CallPop; recipient: boolean } | undefined) => (prev ? { ...prev, pop } : prev))
    queryClient.invalidateQueries({ queryKey: ['calls'] })
    queryClient.invalidateQueries({ queryKey: ['person-calls'] })
  }
}

function Identity({ pop, onCreate, onOther }: { pop: CallPop; onCreate: () => void; onOther: () => void }) {
  const update = usePopMutation()
  // Everyone offered here already holds the number, except a confirmed
  // person whose record lacks it ("Save number to their record").
  const confirm = useMutation({ mutationFn: (personId: string) => callsApi.confirm(pop.call.id, personId, pop.can.rememberNumber), onSuccess: (r) => update(r.pop) })
  const known = pop.call.person

  if (known) {
    const confirmed = known.method === 'staff_confirmed'
    const holds = pop.candidates.some((c) => c.personId === known.personId)
    const others = pop.candidates.filter((c) => c.personId !== known.personId)
    return (
      <div className="call-who">
        <span className="avatar call-avatar" aria-hidden>
          {initials(known.displayName)}
        </span>
        <div className="call-who-text">
          <div className="call-who-name">
            <span className="call-who-lead">{confirmed ? 'Caller' : 'Likely'}</span> <Link to={`/people/${known.personId}`}>{known.displayName}</Link>
          </div>
          <div className="faint">
            {confirmed ? 'Confirmed by staff' : 'The only person in Hub with this number'}
            {confirmed && pop.call.outsideNumber && (holds ? ' · number on their record' : ' · number not on their record')}
            {confirmed && others.length > 0 && ` · also on ${others.map((o) => o.displayName).join(', ')}’s record`}
            {pop.can.confirm && (
              <>
                {' · '}
                <button type="button" className="link-button faint" onClick={onOther}>
                  someone else?
                </button>
              </>
            )}
          </div>
        </div>
        {!confirmed && pop.can.confirm && (
          <button type="button" className="btn btn-compact" disabled={confirm.isPending} onClick={() => confirm.mutate(known.personId)} title={`Record that this call was ${known.displayName}`}>
            <Check size={14} aria-hidden /> Confirm
          </button>
        )}
        {confirmed && !holds && pop.can.rememberNumber && (
          <button
            type="button"
            className="btn btn-compact"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate(known.personId)}
            title={`Add ${formatCallNumber(pop.call.outsideNumber)} to ${known.displayName}’s phones`}
          >
            Save number to their record
          </button>
        )}
        {confirm.error && <MutationError error={confirm.error} />}
      </div>
    )
  }

  if (pop.candidates.length > 1) {
    return (
      <div className="call-who call-who-many">
        <div className="call-who-name">
          <span className="call-who-lead">Who is it?</span> {pop.candidates.length} people in Hub have this number
        </div>
        <ul className="call-candidates">
          {pop.candidates.map((c) => (
            <li key={c.personId}>
              <span className="avatar call-avatar-sm" aria-hidden>
                {initials(c.displayName)}
              </span>
              <Link to={`/people/${c.personId}`}>{c.displayName}</Link>
              {pop.can.confirm && (
                <button type="button" className="btn btn-compact" disabled={confirm.isPending} onClick={() => confirm.mutate(c.personId)}>
                  This was them
                </button>
              )}
            </li>
          ))}
        </ul>
        {confirm.error && <MutationError error={confirm.error} />}
        {pop.can.confirm && (
          <button type="button" className="link-button faint call-who-other" onClick={onOther}>
            Someone else?
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="call-who call-who-new">
      <span className="avatar call-avatar call-avatar-new" aria-hidden>
        ?
      </span>
      <div className="call-who-text">
        <div className="call-who-name">
          <span className="call-who-lead">New caller</span>
        </div>
        <div className="faint">No one in Hub has this number.</div>
      </div>
      <div className="row call-who-actions">
        {pop.can.createPerson && (
          <button type="button" className="btn btn-compact btn-primary" onClick={onCreate}>
            <UserPlus size={14} aria-hidden /> Create person
          </button>
        )}
        {pop.can.confirm && (
          <button type="button" className="btn btn-compact" onClick={onOther}>
            Find person
          </button>
        )}
      </div>
    </div>
  )
}

function MutationError({ error }: { error: unknown }) {
  return <p className="call-error">{error instanceof ApiError ? error.message : 'That didn’t work. Try again.'}</p>
}

// ------------------------------------------------------- caller context

// What the person page already gathers, cut to what helps on a call. Each
// part arrives on its own; a slow app never holds up the rest.
export function CallerContext({ personId }: { personId: string }) {
  const canCustomers = useCan('customers.read')
  const canPeople = useCan('people.read')
  const canHistory = canCustomers && canPeople
  const canCases = useCan('support.read')
  const canComms = useCan('comms.read')
  const history = useQuery({ queryKey: ['history', personId], queryFn: () => api.personHistory(personId), enabled: canHistory, staleTime: 60_000 })
  const cases = useQuery({ queryKey: ['person-cases', personId], queryFn: () => api.personCases(personId), enabled: canCases, staleTime: 60_000 })
  const comms = useQuery({ queryKey: ['person-comms', personId], queryFn: () => commsApi.personPanel(personId), enabled: canComms, staleTime: 60_000 })
  if (!canHistory && !canCases && !canComms) return null

  const rows: ReactNode[] = []
  const h = history.data
  if (canHistory && history.isLoading) rows.push(<Skeleton key="h" />)
  if (h) {
    const ticketing = h.ticketing.status === 'ok' ? h.ticketing.data : null
    const next = ticketing?.tickets.upcoming[0]
    if (ticketing && ticketing.tickets.upcomingCount > 0 && next) {
      rows.push(
        <Fact key="tickets" icon={<Ticket size={14} aria-hidden />} label="Upcoming">
          {next.eventName} · {formatDate(next.performanceStartsAt)}
          {ticketing.tickets.upcomingCount > 1 && <span className="faint"> +{ticketing.tickets.upcomingCount - 1} more</span>}
        </Fact>,
      )
    }
    const booking = h.booking.status === 'ok' ? h.booking.data : null
    const rental = booking?.rentals.upcoming[0]
    if (rental) {
      rows.push(
        <Fact key="rental" icon={<CalendarDays size={14} aria-hidden />} label="Rental">
          {rental.title}
          {rental.eventDate || rental.startsAt ? ` · ${formatDate(rental.eventDate ?? rental.startsAt)}` : ''}
          {rental.status && <span className="faint"> ({rental.status})</span>}
        </Fact>,
      )
    }
    const order = ticketing?.orders.recent[0]
    const posOrder = h.pos.status === 'ok' ? h.pos.data.orders.recent[0] : undefined
    const latest = [order && { at: order.placedAt, text: `BTownTix ${order.reference}${order.eventNames[0] ? ` · ${order.eventNames[0]}` : ''}` }, posOrder && { at: posOrder.placedAt, text: `POS ${posOrder.reference}${posOrder.eventName ? ` · ${posOrder.eventName}` : ''}` }]
      .filter((x): x is { at: string; text: string } => !!x)
      .sort((a, b) => b.at.localeCompare(a.at))[0]
    if (latest) {
      rows.push(
        <Fact key="order" icon={<ShoppingBag size={14} aria-hidden />} label="Last order">
          {latest.text} · {formatDate(latest.at)}
        </Fact>,
      )
    }
    const loyalty = h.loyalty.status === 'ok' ? h.loyalty.data : null
    if (loyalty?.account) {
      const drinks = loyalty.entitlements.reduce((n, e) => n + e.remaining, 0)
      rows.push(
        <Fact key="rewards" icon={<Gift size={14} aria-hidden />} label="Rewards">
          {loyalty.account.pointsBalance.toLocaleString('en-US')} points{loyalty.account.tierName ? ` · ${loyalty.account.tierName}` : ''}
          {drinks > 0 && ` · ${drinks} drink ticket${drinks === 1 ? '' : 's'}`}
        </Fact>,
      )
    }
    const down = (['ticketing', 'loyalty', 'pos', 'booking'] as const).filter((k) => h[k].status === 'unavailable').length
    if (down > 0) rows.push(<p key="down" className="faint call-fact-note">{down === 1 ? 'One app' : `${down} apps`} didn’t answer.</p>)
  }
  if (canCases && cases.isLoading) rows.push(<Skeleton key="c" />)
  const open = (cases.data?.cases ?? []).filter((c) => c.status !== 'resolved' && c.status !== 'closed')
  if (open.length > 0) {
    rows.push(
      <Fact key="cases" icon={<LifeBuoy size={14} aria-hidden />} label="Open case">
        <Link to={`/cases/${open[0]!.id}`}>
          #{open[0]!.number} {open[0]!.subject}
        </Link>
        {open.length > 1 && <span className="faint"> +{open.length - 1} more</span>}
      </Fact>,
    )
  }
  if (canComms && comms.isLoading) rows.push(<Skeleton key="m" />)
  if (comms.data) {
    const suppressed = comms.data.addresses.filter((a) => a.suppressions.length > 0)
    const optedIn = new Set(comms.data.addresses.flatMap((a) => a.states.filter((s) => s.state === 'granted' && s.purposeKey === 'marketing').map((s) => (s.channel === 'sms' ? 'texts' : s.channel))))
    if (suppressed.length > 0 || optedIn.size > 0) {
      rows.push(
        <Fact key="comms" icon={<Mail size={14} aria-hidden />} label="Messages">
          {optedIn.size > 0 ? `Opted in to ${[...optedIn].join(' and ')}` : 'Not opted in'}
          {suppressed.length > 0 && <span className="pill pill-bad call-pill">Do not contact: {suppressed.map((a) => a.kind).join(', ')}</span>}
        </Fact>,
      )
    }
  }
  const loaded = (!canHistory || !history.isLoading) && (!canCases || !cases.isLoading) && (!canComms || !comms.isLoading)
  if (loaded && rows.length === 0) rows.push(<p key="none" className="faint call-fact-note">Nothing to show.</p>)
  return <div className="call-facts">{rows}</div>
}

function Fact({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="call-fact">
      <span className="call-fact-icon">{icon}</span>
      <span className="call-fact-label">{label}</span>
      <span className="call-fact-value">{children}</span>
    </div>
  )
}

function Skeleton() {
  return <div className="call-skeleton" aria-hidden />
}

// --------------------------------------------------------------- dialogs

function NoteForm({ callId, onDone }: { callId: string; onDone: () => void }) {
  const update = usePopMutation()
  const [body, setBody] = useState('')
  const save = useMutation({
    mutationFn: () => callsApi.note(callId, body.trim()),
    onSuccess: (r) => {
      update(r.pop)
      onDone()
    },
  })
  return (
    <form
      className="call-note-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (body.trim()) save.mutate()
      }}
    >
      <label htmlFor={`note-${callId}`} className="sr-only">
        Note on this call
      </label>
      <textarea id={`note-${callId}`} className="input" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What was the call about?" autoFocus maxLength={2000} />
      {save.error && <MutationError error={save.error} />}
      <div className="row">
        <button type="submit" className="btn btn-primary btn-compact" disabled={!body.trim() || save.isPending}>
          Save note
        </button>
        <button type="button" className="btn btn-quiet btn-compact" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function CreatePersonDialog({ pop, suggested, onClose }: { pop: CallPop; suggested: string; onClose: () => void }) {
  const update = usePopMutation()
  const [name, setName] = useState(suggested)
  const [email, setEmail] = useState('')
  const create = useMutation({
    mutationFn: () => callsApi.createPerson(pop.call.id, { displayName: name.trim(), ...(email.trim() ? { email: email.trim() } : {}) }),
    onSuccess: (r) => {
      update(r.pop)
      onClose()
    },
  })
  return (
    <Dialog title="Create person" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <p className="muted" style={{ margin: 0 }}>
          Phone: <strong>{formatCallNumber(pop.call.outsideNumber)}</strong>
        </p>
        <div className="field">
          <label htmlFor="cp-name">Name</label>
          <input id="cp-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={200} />
        </div>
        <div className="field">
          <label htmlFor="cp-email">Email (optional)</label>
          <input id="cp-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} />
        </div>
        {create.error && <MutationError error={create.error} />}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={!name.trim() || create.isPending}>
            Create and link to this call
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function ConfirmOtherDialog({ pop, onClose }: { pop: CallPop; onClose: () => void }) {
  const update = usePopMutation()
  const [picked, setPicked] = useState<{ id: string; displayName: string } | null>(null)
  const [remember, setRemember] = useState(true)
  const number = formatCallNumber(pop.call.outsideNumber)
  const holds = !!picked && pop.candidates.some((c) => c.personId === picked.id)
  const others = pop.candidates.filter((c) => c.personId !== picked?.id)
  // Offered only to staff who may edit people (people.manage), and only when
  // the call has a real number the picked person doesn't already have.
  const offer = pop.can.rememberNumber && !!picked && !holds
  const confirm = useMutation({
    mutationFn: (p: { id: string; displayName: string }) => callsApi.confirm(pop.call.id, p.id, offer && remember),
    onSuccess: (r) => {
      update(r.pop)
      if (r.numberSaved !== 'added') onClose()
    },
  })

  if (confirm.data?.numberSaved === 'added' && picked) {
    return (
      <Dialog title="Who was it?" onClose={onClose}>
        <div className="stack">
          <div className="notice notice-ok" role="status">
            <Check size={16} aria-hidden />
            <div>
              Saved {number} to {picked.displayName}’s record.
              {others.length > 0 && <p>{`${others.map((o) => o.displayName).join(', ')} ${others.length === 1 ? 'has' : 'have'} it too.`}</p>}
            </div>
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-primary" onClick={onClose} autoFocus>
              Done
            </button>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog title="Who was it?" onClose={onClose}>
      <div className="stack">
        {!picked ? (
          <>
            <PersonPicker label="Person" onPick={(p) => setPicked({ id: p.id, displayName: p.displayName })} />
          </>
        ) : (
          <>
            <p style={{ margin: 0 }}>
              This call was <strong>{picked.displayName}</strong>.{' '}
              <button type="button" className="link-button faint" onClick={() => setPicked(null)}>
                Pick someone else
              </button>
            </p>
            {holds && (
              <p className="faint" style={{ margin: 0 }}>
                {number} is already on their record.
              </p>
            )}
            {offer && (
              <label className="check-row">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span>
                  Save {number} to {picked.displayName}’s record
                  {remember && others.length > 0 && (
                    <span className="faint" style={{ display: 'block' }}>
                      {`${others.map((o) => o.displayName).join(', ')} ${others.length === 1 ? 'has' : 'have'} this number too.`}
                    </span>
                  )}
                </span>
              </label>
            )}
            {!pop.can.rememberNumber && pop.call.outsideNumber && !holds && (
              <p className="faint" style={{ margin: 0 }}>
                Saving the number needs people.manage.
              </p>
            )}
            {confirm.error && <MutationError error={confirm.error} />}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={confirm.isPending} onClick={() => confirm.mutate(picked)}>
                <Check size={14} aria-hidden /> Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
